import type { Database } from "@/lib/database.types";
import type { SubscriptionTier } from "@/types/projects";

export type StoredEntityCardType = Database["public"]["Enums"]["entity_type"];
export type StoredRelationshipKind = Database["public"]["Enums"]["relationship_kind"];
export type StoredRelationshipType = "entity_entity" | "entity_lore" | "lore_lore";
export type EntityCardType = "character" | "item-object" | "creature" | "location" | "machine";
export type EntityItemCategory = "weapon" | "artifact" | "tool" | "relic" | "technology";
export type EntityRelationshipTargetKind = "entity-card" | "lore-entry";
export type BuiltInEntityRelationshipLabel =
  | "ally"
  | "rival"
  | "family"
  | "romantic"
  | "member-of"
  | "belongs-to"
  | "created-by"
  | "held-by"
  | "opposes";
export type EntityRelationshipLabel = BuiltInEntityRelationshipLabel | "custom";

export type EntityCardRecord = Database["public"]["Tables"]["entity_cards"]["Row"];
export type EntityCardInsert = Database["public"]["Tables"]["entity_cards"]["Insert"];
export type EntityCardUpdate = Database["public"]["Tables"]["entity_cards"]["Update"];
export type EntityRelationshipRecord = Database["public"]["Tables"]["relationships"]["Row"];
export type EntityRelationshipInsert = Database["public"]["Tables"]["relationships"]["Insert"];
export type EntityRelationshipUpdate = Database["public"]["Tables"]["relationships"]["Update"];
export type EntityLoreType = Database["public"]["Enums"]["lore_type"];
export type EntityLoreCanonStatus = Database["public"]["Enums"]["canon_status"];

export interface CharacterEntityData {
  aliases: string[];
  ageText: string | null;
  notes: string | null;
  personalityTraits: string | null;
  physicalDescription: string | null;
  raceLoreEntryId: string | null;
  skillsAndAbilities: string | null;
}

export interface ItemEntityData {
  currentHolderEntityCardId: string | null;
  description: string | null;
  itemCategory: EntityItemCategory;
  origin: string | null;
}

export interface CreatureEntityData {
  abilities: string | null;
  description: string | null;
  speciesLoreEntryId: string | null;
}

export interface LocationEntityData {
  description: string | null;
  notes: string | null;
}

export interface MachineEntityData {
  capabilities: string | null;
  currentHolderEntityCardId: string | null;
  description: string | null;
  origin: string | null;
}

export type EntityCardData =
  | CharacterEntityData
  | ItemEntityData
  | CreatureEntityData
  | LocationEntityData
  | MachineEntityData;

export interface EntityCardLinkView {
  broken: boolean;
  href: string | null;
  targetId: string;
  targetStatusLabel: string | null;
  targetTitle: string;
}

export interface EntityRelationshipView {
  broken: boolean;
  createdAt: string;
  customLabel: string | null;
  href: string | null;
  id: string;
  label: EntityRelationshipLabel;
  labelText: string;
  targetId: string;
  targetKind: EntityRelationshipTargetKind;
  targetStatusLabel: string | null;
  targetTitle: string;
}

export interface EntityCardView {
  ageText: string;
  aliases: string[];
  createdAt: string;
  creatureAbilities: string;
  creatureDescription: string;
  currentHolderLink: EntityCardLinkView | null;
  generateActionLabel: string | null;
  hasBrokenLinks: boolean;
  hasImage: boolean;
  id: string;
  imagePath: string | null;
  imageUrl: string | null;
  itemCategory: EntityItemCategory | null;
  itemDescription: string;
  locationDescription: string;
  locationNotes: string;
  machineCapabilities: string;
  machineDescription: string;
  name: string;
  notes: string;
  origin: string;
  personalityTraits: string;
  physicalDescription: string;
  projectId: string;
  raceLink: EntityCardLinkView | null;
  relationshipCount: number;
  relationships: EntityRelationshipView[];
  secondaryText: string;
  skillsAndAbilities: string;
  speciesLink: EntityCardLinkView | null;
  type: EntityCardType;
  typeLabel: string;
  updatedAt: string;
}

export interface ProjectEntityCapacitySummary {
  entityCardsLimit: number | null;
  entityCardsRemaining: number | null;
  entityCardsUsed: number;
  isUnlimited: boolean;
  tier: SubscriptionTier;
}

export interface EntityLimitPrompt {
  allowedCount: number;
  currentCount: number;
  reason: "entity_card_limit_reached";
  recommendedTier: SubscriptionTier;
  tier: SubscriptionTier;
}

export interface EntityLibraryFilters {
  searchText: string;
  typeFilter: EntityCardType | "all";
}

export interface EntityCardEditorInput {
  ageText?: string;
  aliases?: string[];
  creatureAbilities?: string;
  creatureDescription?: string;
  currentHolderEntityCardId?: string | null;
  imageFile?: File | null;
  itemCategory?: EntityItemCategory | null;
  itemDescription?: string;
  locationDescription?: string;
  locationNotes?: string;
  machineCapabilities?: string;
  machineDescription?: string;
  name: string;
  notes?: string;
  origin?: string;
  personalityTraits?: string;
  physicalDescription?: string;
  raceLoreEntryId?: string | null;
  removeImage?: boolean;
  skillsAndAbilities?: string;
  speciesLoreEntryId?: string | null;
  type: EntityCardType;
}

export interface CreateEntityCardInput extends EntityCardEditorInput {
  projectId: string;
}

export type UpdateEntityCardInput = EntityCardEditorInput;

export interface EntityRelationshipDraft {
  customLabel: string;
  label: EntityRelationshipLabel;
  targetId: string;
  targetKind: EntityRelationshipTargetKind;
}

export interface CreateEntityRelationshipInput extends EntityRelationshipDraft {
  projectId: string;
  sourceEntityCardId: string;
}

export type UpdateEntityRelationshipInput = EntityRelationshipDraft;

export interface EntityCardTargetOption {
  description: string;
  id: string;
  name: string;
  type: EntityCardType;
}

export interface EntityLoreOption {
  canonStatus: EntityLoreCanonStatus;
  canonStatusLabel: string;
  id: string;
  title: string;
  type: EntityLoreType;
}

export interface EntityRelationshipTargetOption {
  description: string;
  href: string | null;
  id: string;
  kind: EntityRelationshipTargetKind;
  statusLabel: string | null;
  title: string;
}

export interface EntityWorkspaceSnapshot {
  capacity: ProjectEntityCapacitySummary;
  cards: EntityCardView[];
  characterTargetOptions: EntityCardTargetOption[];
  raceSpeciesOptions: EntityLoreOption[];
  relationshipTargetOptions: EntityRelationshipTargetOption[];
}

export const entityCardTypes: EntityCardType[] = [
  "character",
  "item-object",
  "creature",
  "location",
  "machine",
];
export const entityItemCategories: EntityItemCategory[] = [
  "weapon",
  "artifact",
  "tool",
  "relic",
  "technology",
];
export const entityRelationshipBuiltInLabels: BuiltInEntityRelationshipLabel[] = [
  "ally",
  "rival",
  "family",
  "romantic",
  "member-of",
  "belongs-to",
  "created-by",
  "held-by",
  "opposes",
];
export const entityRelationshipLabels: EntityRelationshipLabel[] = [
  ...entityRelationshipBuiltInLabels,
  "custom",
];

export const entityCardTypeLabels: Record<EntityCardType, string> = {
  character: "Character",
  "item-object": "Item / Object",
  creature: "Creature",
  location: "Location",
  machine: "Machine",
};

export const entityCardTypeLabelKeys: Record<EntityCardType, string> = {
  character: "entities.types.character",
  "item-object": "entities.types.itemObject",
  creature: "entities.types.creature",
  location: "entities.types.location",
  machine: "entities.types.machine",
};

export const entityCardTypeDescriptions: Record<EntityCardType, string> = {
  character:
    "Capture identity, race links, appearance, personality, skills, and writer notes for a recurring participant.",
  "item-object":
    "Track an artifact, weapon, tool, relic, or technology record and optionally connect it to the current holder.",
  creature:
    "Describe a species-linked creature with its abilities, behavior, and other field notes.",
  location:
    "Describe a place as a linkable entity with its terrain, function, landmarks, and story notes.",
  machine:
    "Track a vehicle, engine, construct, device, or other machine with capabilities, origin, and operator context.",
};

export const entityCardTypeDescriptionKeys: Record<EntityCardType, string> = {
  character: "entities.typeDescriptions.character",
  "item-object": "entities.typeDescriptions.itemObject",
  creature: "entities.typeDescriptions.creature",
  location: "entities.typeDescriptions.location",
  machine: "entities.typeDescriptions.machine",
};

export const entityItemCategoryLabels: Record<EntityItemCategory, string> = {
  artifact: "Artifact",
  relic: "Relic",
  technology: "Technology",
  tool: "Tool",
  weapon: "Weapon",
};

export const entityItemCategoryLabelKeys: Record<EntityItemCategory, string> = {
  artifact: "entities.itemCategories.artifact",
  relic: "entities.itemCategories.relic",
  technology: "entities.itemCategories.technology",
  tool: "entities.itemCategories.tool",
  weapon: "entities.itemCategories.weapon",
};

export const entityRelationshipLabelLabels: Record<EntityRelationshipLabel, string> = {
  ally: "Ally",
  "belongs-to": "Belongs to",
  "created-by": "Created by",
  custom: "Custom",
  family: "Family",
  "held-by": "Held by",
  "member-of": "Member of",
  opposes: "Opposes",
  romantic: "Romantic",
  rival: "Rival",
};

export const entityRelationshipLabelKeys: Record<EntityRelationshipLabel, string> = {
  ally: "entities.relationships.labels.ally",
  "belongs-to": "entities.relationships.labels.belongsTo",
  "created-by": "entities.relationships.labels.createdBy",
  custom: "entities.relationships.labels.custom",
  family: "entities.relationships.labels.family",
  "held-by": "entities.relationships.labels.heldBy",
  "member-of": "entities.relationships.labels.memberOf",
  opposes: "entities.relationships.labels.opposes",
  romantic: "entities.relationships.labels.romantic",
  rival: "entities.relationships.labels.rival",
};

export const entityRelationshipTargetKindLabelKeys: Record<
  EntityRelationshipTargetKind,
  string
> = {
  "entity-card": "entities.relationships.targetKinds.entityCard",
  "lore-entry": "entities.relationships.targetKinds.loreEntry",
};

export const emptyEntityFilters: EntityLibraryFilters = {
  searchText: "",
  typeFilter: "all",
};

export function toStoredEntityCardType(type: EntityCardType): StoredEntityCardType {
  return type === "item-object" ? "item" : type;
}

export function fromStoredEntityCardType(type: StoredEntityCardType): EntityCardType {
  return type === "item" ? "item-object" : type;
}

export function toStoredRelationshipKind(kind: EntityRelationshipTargetKind): StoredRelationshipKind {
  return kind === "entity-card" ? "entity" : "lore";
}

export function fromStoredRelationshipKind(kind: StoredRelationshipKind): EntityRelationshipTargetKind {
  return kind === "entity" ? "entity-card" : "lore-entry";
}

export function createEntityDraft(type: EntityCardType): Omit<CreateEntityCardInput, "projectId"> {
  switch (type) {
    case "character":
      return {
        ageText: "",
        aliases: [],
        imageFile: null,
        name: "",
        notes: "",
        personalityTraits: "",
        physicalDescription: "",
        raceLoreEntryId: null,
        removeImage: false,
        skillsAndAbilities: "",
        type,
      };
    case "item-object":
      return {
        currentHolderEntityCardId: null,
        imageFile: null,
        itemCategory: null,
        itemDescription: "",
        name: "",
        origin: "",
        removeImage: false,
        type,
      };
    case "creature":
      return {
        creatureAbilities: "",
        creatureDescription: "",
        imageFile: null,
        name: "",
        removeImage: false,
        speciesLoreEntryId: null,
        type,
      };
    case "location":
      return {
        imageFile: null,
        locationDescription: "",
        locationNotes: "",
        name: "",
        removeImage: false,
        type,
      };
    case "machine":
      return {
        currentHolderEntityCardId: null,
        imageFile: null,
        machineCapabilities: "",
        machineDescription: "",
        name: "",
        origin: "",
        removeImage: false,
        type,
      };
    default:
      return {
        imageFile: null,
        name: "",
        removeImage: false,
        type,
      };
  }
}

export function createEntityRelationshipDraft(): EntityRelationshipDraft {
  return {
    customLabel: "",
    label: "ally",
    targetId: "",
    targetKind: "entity-card",
  };
}

export function formatEntityAliasesInput(aliases: string[]) {
  return aliases.join("\n");
}

export function parseEntityAliasesInput(value: string) {
  return value
    .split(/[\n,]/)
    .map((segment) => segment.trim())
    .filter(Boolean);
}

export function getEntityCardHref(projectId: string, cardId: string) {
  return `/projects/${projectId}/entities/cards/${cardId}`;
}

export function getEntityLoreHref(projectId: string, loreEntryId: string) {
  return `/projects/${projectId}/lore/entries/${loreEntryId}`;
}

export function createMissingEntityTargetTitle(targetId: string) {
  return `Missing Entity Card (${targetId})`;
}

export function createMissingLoreTargetTitle(targetId: string) {
  return `Missing Lore Entry (${targetId})`;
}
